import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import {
  X, Sparkles, Clock, FileText, CheckCircle2, XCircle, GitMerge,
  Receipt, BookOpen, Briefcase, AlertTriangle, Mail, ListChecks,
} from 'lucide-react';

/**
 * Patch 18.1 — Unified Quotation Timeline Drawer
 * =================================================
 * Read-only narrative drawer that renders the FULL storyline of a
 * quotation: Lead/RFQ origin → Quotation lifecycle → Approvals →
 * Project linkage → Invoices → Journals → Audit trail entries.
 *
 * Pulls from a single backend composer (GET /quotations/{id}/full-timeline)
 * so the UI is no longer fragmented across Audit / Approval / Invoice
 * drawers — every actor (CFO, Sales, Finance) sees the same single
 * chronological story.
 */

// Visual map: every event type gets a consistent icon + color scheme.
// Source-driven defaults keep unknown types from looking broken.
const SOURCE_THEME = {
  lead:       { ring: 'ring-amber-200',   dot: 'bg-amber-500',   text: 'text-amber-900',   chip: 'bg-amber-50 text-amber-800 border-amber-200' },
  rfq:        { ring: 'ring-indigo-200',  dot: 'bg-indigo-500',  text: 'text-indigo-900',  chip: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  manual:     { ring: 'ring-slate-200',   dot: 'bg-slate-400',   text: 'text-slate-900',   chip: 'bg-slate-50 text-slate-700 border-slate-200' },
  system:     { ring: 'ring-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-900', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  accounting: { ring: 'ring-violet-200',  dot: 'bg-violet-500',  text: 'text-violet-900',  chip: 'bg-violet-50 text-violet-800 border-violet-200' },
  audit:      { ring: 'ring-rose-200',    dot: 'bg-rose-500',    text: 'text-rose-900',    chip: 'bg-rose-50 text-rose-800 border-rose-200' },
};

const TYPE_ICON = {
  lead_created:           Sparkles,
  rfq_created:            ListChecks,
  manual_creation:        FileText,
  converted_to_quotation: GitMerge,
  quotation_created:      FileText,
  quotation_approved:     CheckCircle2,
  quotation_rejected:     XCircle,
  quotation_superseded:   GitMerge,
  invoice_created:        Receipt,
  journal_posted:         BookOpen,
  project_linked:         Briefcase,
};

const themeFor = (ev) => SOURCE_THEME[ev.source] || SOURCE_THEME.system;
const iconFor  = (ev) => TYPE_ICON[ev.type] || Clock;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const UnifiedQuotationTimeline = ({ quotationId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);
  const [filter, setFilter]   = useState('all'); // all | lead | rfq | system | accounting

  useEffect(() => {
    if (!open || !quotationId) return;
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    axiosInstance
      .get(`/quotations/${quotationId}/full-timeline`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load timeline');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, quotationId]);

  if (!open) return null;

  const events    = data?.events || [];
  const filtered  = filter === 'all' ? events : events.filter((e) => e.source === filter);
  const counts    = events.reduce((acc, e) => {
    acc[e.source] = (acc[e.source] || 0) + 1; return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      data-testid="unified-timeline-drawer-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col"
        data-testid="unified-timeline-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                <Sparkles className="w-4 h-4" /> Decision Narrative
              </div>
              <h2 className="text-xl font-bold text-gray-900 mt-1">
                {data?.quotation?.quotation_no || 'Quotation Timeline'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {data?.quotation?.customer_name || '—'}
                {data?.quotation?.source && (
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${SOURCE_THEME[data.quotation.source]?.chip || SOURCE_THEME.manual.chip}`}>
                    Source: {data.quotation.source.toUpperCase()}
                    {data.quotation.source === 'lead'  && data.quotation.source_lead_no ? ` · ${data.quotation.source_lead_no}` : ''}
                    {data.quotation.source === 'rfq'   && data.quotation.source_rfq_no  ? ` · ${data.quotation.source_rfq_no}`  : ''}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="unified-timeline-close"
              aria-label="Close timeline"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Filter chips */}
          {!loading && !error && events.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="unified-timeline-filters">
              {['all', 'lead', 'rfq', 'system', 'accounting', 'manual', 'audit'].map((k) => {
                if (k !== 'all' && !counts[k]) return null;
                const active = filter === k;
                return (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    data-testid={`unified-timeline-filter-${k}`}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      active
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {k === 'all' ? `All (${events.length})` : `${k} (${counts[k]})`}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="text-center py-20 text-sm text-gray-500" data-testid="unified-timeline-loading">
              Loading narrative…
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-lg p-4 flex items-start gap-2" data-testid="unified-timeline-error">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-20 text-sm text-gray-400" data-testid="unified-timeline-empty">
              No events to display.
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <ol className="relative border-l-2 border-slate-100 ml-3 space-y-5" data-testid="unified-timeline-list">
              {filtered.map((ev, idx) => {
                const theme = themeFor(ev);
                const Icon  = iconFor(ev);
                return (
                  <li
                    key={`${ev.type}-${idx}`}
                    className="ml-5"
                    data-testid={`unified-timeline-event-${idx}`}
                  >
                    {/* Dot + icon */}
                    <span className={`absolute -left-3.5 mt-1.5 flex items-center justify-center w-7 h-7 rounded-full ring-4 ${theme.ring} ${theme.dot}`}>
                      <Icon className="w-3.5 h-3.5 text-white" />
                    </span>

                    <div className={`rounded-lg border border-gray-200 bg-white p-3 hover:shadow-sm transition-shadow`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold ${theme.text}`}>
                            {ev.label || ev.type}
                          </div>
                          {ev.summary && (
                            <div className="text-xs text-gray-600 mt-0.5 break-words">
                              {ev.summary}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-gray-500">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded border ${theme.chip}`}>
                              {ev.source}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {fmtTime(ev.timestamp)}
                            </span>
                            {ev.actor && (
                              <span className="inline-flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {ev.actor}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Metadata pills (small, only the meaningful ones) */}
                      {ev.metadata && Object.keys(ev.metadata).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {Object.entries(ev.metadata)
                            .filter(([, v]) => v !== null && v !== undefined && v !== '')
                            .slice(0, 6)
                            .map(([k, v]) => (
                              <span
                                key={k}
                                className="text-[10px] font-mono bg-slate-50 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded"
                              >
                                {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Footer summary */}
        <div className="px-6 py-3 border-t border-gray-200 bg-slate-50 text-xs text-gray-600 flex items-center justify-between" data-testid="unified-timeline-footer">
          <span>
            {data?.event_count ?? 0} event{(data?.event_count ?? 0) === 1 ? '' : 's'} • read-only narrative
          </span>
          {data?.quotation?.total !== undefined && data?.quotation?.total !== null && (
            <span className="font-medium text-gray-700">
              Total: {Number(data.quotation.total).toLocaleString()} SAR
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedQuotationTimeline;
